import { FC } from 'react';
import { ContactAvatar } from '../../contact_avatar/ContactAvatar';
import { Col, Row } from 'antd';
import * as React from 'react';
import { compareStrings } from '../../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { statusIndicatorFormatter } from '../../../utils/DataFormatterUtils';

export enum UserCardSize {
	DEFAULT = 'default',
	LARGE = 'large',
	SMALL = 'small',
}

interface UserCardSizeConfig {
	avatarFontSize: number;
	avatarMinWidth: number;
	avatarSize: number;
	nameFontSize: number;
	nameFontSizeNoSubTitle: number;
	subTitleFontSize: number;
	detailFontSize: number;
}

const USER_CARD_SIZE_MAP: Record<UserCardSize, UserCardSizeConfig> = {
	[UserCardSize.DEFAULT]: {
		avatarFontSize: 14,
		avatarMinWidth: 32,
		avatarSize: 32,
		nameFontSize: 14,
		nameFontSizeNoSubTitle: 17.5,
		subTitleFontSize: 12,
		detailFontSize: 12,
	},
	[UserCardSize.LARGE]: {
		avatarFontSize: 17.5,
		avatarMinWidth: 40,
		avatarSize: 40,
		nameFontSize: 17.5,
		nameFontSizeNoSubTitle: 17.5,
		subTitleFontSize: 14,
		detailFontSize: 14,
	},
	[UserCardSize.SMALL]: {
		avatarFontSize: 9,
		avatarMinWidth: 16,
		avatarSize: 16,
		nameFontSize: 14,
		nameFontSizeNoSubTitle: 17.5,
		subTitleFontSize: 12,
		detailFontSize: 12,
	},
};

interface UserCardProps {
	contact: any;
	userType: any;
	name: any;
	subTitle?: string;
	showAvatar?: boolean;
	size?: 'default' | 'large';
	mode?: 'full' | 'simple' | 'avatar';
	details?: any;
	extraDetails?: any;
	onClick?: any;
	account?: any;
	status?: any;
	avatarSize?: number;
}

function formatPhoneNumber(phoneNumberString) {
	var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
	var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	if (match) {
		var intlCode = match[1] ? '+1 ' : '';
		return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
	}
	return null;
}

export const TITLE_DISPLAY = {
	key: 'title',
	display: 'Title',
	formatValue: (value) => value,
};
export const DEPARTMENT_DISPLAY = {
	key: 'department',
	display: 'Department',
	formatValue: (value) => value,
};
export const MOBILE_PHONE_DISPLAY = {
	key: 'phoneMobile',
	display: 'Mobile',
	formatValue: (value) => formatPhoneNumber(value),
};
export const WORK_PHONE_DISPLAY = {
	key: 'phoneWork',
	display: 'Work',
	formatValue: (value) => formatPhoneNumber(value),
};
export const EMAIL_DISPLAY = {
	key: 'email',
	display: 'Email',
	formatValue: (value) => value,
};

export const UserCard: FC<UserCardProps> = ({
	contact,
	userType,
	name,
	subTitle,
	showAvatar = true,
	size = 'default',
	mode = 'simple',
	details = [TITLE_DISPLAY, DEPARTMENT_DISPLAY, MOBILE_PHONE_DISPLAY, WORK_PHONE_DISPLAY],
	extraDetails = [],
	onClick,
	account,
	status,
	avatarSize,
}) => {
	if (!contact) {
		return null;
	}

	const cardSize = (size || UserCardSize.DEFAULT) as UserCardSize;
	const sizeConfig = USER_CARD_SIZE_MAP[cardSize];

	const detailList = details
		.filter((obj) => !!contact.hasOwnProperty(obj.key) && contact[obj.key])
		.map((obj) => ({
			...obj,
			value: obj.hasOwnProperty('formatValue')
				? obj.formatValue(contact[obj.key])
				: contact[obj.key],
		}));

	return mode === 'avatar' ? (
		<ContactAvatar
			hidePopover={true}
			style={{
				fontSize: sizeConfig.avatarFontSize,
				minWidth: sizeConfig.avatarMinWidth,
			}}
			size={sizeConfig.avatarSize}
			contact={contact}
			userType={userType}
		/>
	) : (
		<span
			onClick={onClick}
			style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}
		>
			<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
				{showAvatar ? (
					<ContactAvatar
						hidePopover={true}
						style={{
							marginRight: 8,
							fontSize: sizeConfig.avatarFontSize,
							minWidth: sizeConfig.avatarMinWidth,
						}}
						size={avatarSize || sizeConfig.avatarSize}
						contact={contact}
						userType={userType}
					/>
				) : null}
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
					<Row
						style={{
							justifyContent: 'flex-start',
							color: 'rgba(0,0,0,0.65)',
							fontSize: subTitle ? sizeConfig.nameFontSize : sizeConfig.nameFontSizeNoSubTitle,
						}}
					>
						{status ? (
							<span>
								<span>{name}</span>
								<span
									style={{
										marginLeft: 8,
										fontSize: 12,
										textDecoration: 'none',
										display: 'inline-block',
									}}
								>
									{statusIndicatorFormatter(
										compareStrings(status, 'invited'),
										compareStrings(status, 'active'),
										true,
										8,
										{ marginRight: 4 }
									)}
								</span>
							</span>
						) : (
							<Ellipsis fullWidthRecognition={true} lines={1}>
								{name}
							</Ellipsis>
						)}
					</Row>
					<Row
						style={{
							justifyContent: 'flex-start',
							color: 'rgba(0,0,0,0.45)',
							fontSize: sizeConfig.subTitleFontSize,
						}}
					>
						<Ellipsis fullWidthRecognition={true} lines={1}>
							{subTitle}
						</Ellipsis>
					</Row>
				</div>
			</div>
			{mode === 'full' && detailList.length > 0 ? (
				<Row
					style={{
						gap: 8,
						borderTop: '1px solid #e8e8e8',
						width: '100%',
						marginTop: 4,
						paddingTop: 4,
					}}
				>
					<Col>
						{detailList.map((obj) => (
							<Row
								style={{
									color: 'rgba(0,0,0,0.45)',
									fontSize: sizeConfig.detailFontSize,
									justifyContent: 'flex-end',
								}}
							>
								{obj.display}
							</Row>
						))}
					</Col>
					<Col>
						{detailList.map((obj) => (
							<Row
								style={{
									color: 'rgba(0,0,0,0.65)',
									fontSize: sizeConfig.detailFontSize,
								}}
							>
								<Ellipsis fullWidthRecognition={true} lines={1}>
									{obj.value}
								</Ellipsis>
							</Row>
						))}
					</Col>
				</Row>
			) : null}
			{extraDetails.length > 0 ? (
				<Row
					style={{
						gap: 8,
						borderTop: '1px solid #e8e8e8',
						width: '100%',
						marginTop: 4,
						paddingTop: 4,
					}}
				>
					<Col>
						{extraDetails.map((obj) => (
							<Row
								style={{
									color: 'rgba(0,0,0,0.45)',
									fontSize: sizeConfig.detailFontSize,
									justifyContent: 'flex-end',
								}}
							>
								{obj.display}
							</Row>
						))}
					</Col>
					<Col>
						{extraDetails.map((obj) => (
							<Row
								style={{
									color: 'rgba(0,0,0,0.65)',
									fontSize: sizeConfig.detailFontSize,
								}}
							>
								<Ellipsis fullWidthRecognition={true} lines={1}>
									{obj.value}
								</Ellipsis>
							</Row>
						))}
					</Col>
				</Row>
			) : null}
		</span>
	);
};
